import Dropdown from '~/components/Shared/Dropdown';
import Actions, { Action } from '~/ui/Actions';
import DownloadIcon from '~/assets/svg/newSvg/actions/download.svg?react';
import { useToastError } from '~/utils/useToastError';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import LoaderSpinner from '../Shared/LoaderSpinner/LoaderSpinner';
import styles from './FoodExportActions.module.scss';
import DeleteIcon from './images/delete-icon.svg?react';
import { Dayjs } from 'dayjs';
import { useToastFoodOrdersUpload } from '~/utils/useToastDownloadOrdersFood';
import { downloadReportFoodFile } from './downloadReportFoodFile';
import { useToastFoodWaresUpload } from '~/utils/useToastDownloadOrderWaresFood';
import { useToastTechnologistUpload } from '~/utils/useToastDownloadOrderTechnologist';
import { instance } from '~/utils/api/api';
import { downloadFile } from '~/utils/downloadFile';
import { ReportDrawer } from '~/components/ReportDrawer/ReportDrawer';

interface XlsFoodExportActions {
  dates?: [Dayjs | null, Dayjs | null];
  foodID?: number;
  onOpenDishesClick?: () => void;
}

interface FoodReportConfig {
  reportType: string;
  title: string;
  reportPath: string;
  reportName: string;
}

const FoodExportActions = ({
  dates,
  foodID,
  onOpenDishesClick,
}: XlsFoodExportActions) => {
  const toastError = useToastError();

  const startDate = dates ? dates[0] : null;
  const endDate = dates ? dates[1] : null;
  const query = foodID ? `&food_id=${foodID}` : '';
  const nameSuffix = foodID ? `-${foodID}` : '';
  const [isLoading, setIsLoading] = useState<{
    isOrdersLoading: boolean;
    isDishesLoading: boolean;
    isTechnologistLoading: boolean;
    isCashierLoading: boolean;
    isFoodTechnologistLoading: boolean;
  }>({
    isOrdersLoading: false,
    isDishesLoading: false,
    isTechnologistLoading: false,
    isCashierLoading: false,
    isFoodTechnologistLoading: false,
  });
  const { t } = useTranslation();

  const { startToast, isActive } = useToastFoodOrdersUpload();
  const { startWaresToast, isWaresActive } = useToastFoodWaresUpload();
  const { startTechnologistToast, isTechnologistActive } =
    useToastTechnologistUpload();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeReport, setActiveReport] = useState<FoodReportConfig | null>(
    null
  );

  const reportConfigs: FoodReportConfig[] = [
    {
      reportType: 'food_orders_export',
      title: `${t('download')} ${t('order_report')}`,
      reportPath: 'food-orders-export?sort' + query,
      reportName: `food-orders${nameSuffix}`,
    },
    {
      reportType: 'food_wares_export',
      title: `${t('download')} ${t('dish_report')}`,
      reportPath: 'food-wares-export?sort' + query,
      reportName: `food-wares${nameSuffix}`,
    },
    {
      reportType: 'food_wares_all_export',
      title: t('order_assembly'),
      reportPath: 'food-wares-all-export?sort' + query,
      reportName: 'technologist',
    },
    {
      reportType: 'food_orders_cashier_export',
      title: t('cashier_report'),
      reportPath: 'food-order-cashier?sort' + query,
      reportName: 'cashier',
    },
    {
      reportType: 'food_technologist_export',
      title: t('technologist_report'),
      reportPath: 'food-technologist-export?' + query,
      reportName: 'food-technologist',
    },
  ];

  const handleOpenDrawer = (config: FoodReportConfig) => {
    setActiveReport(config);
    setDrawerOpen(true);
  };

  const currentFilters = {
    dates: [
      startDate?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      endDate?.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    ],
  };

  const setLoadingByReport = (reportName: string, value: boolean) => {
    if (reportName === 'food-technologist') {
      setIsLoading((prev) => ({ ...prev, isFoodTechnologistLoading: value }));
    } else if (reportName.includes('orders')) {
      setIsLoading((prev) => ({ ...prev, isOrdersLoading: value }));
    } else if (reportName.includes('wares')) {
      setIsLoading((prev) => ({ ...prev, isDishesLoading: value }));
    } else if (reportName.includes('technologist')) {
      setIsLoading((prev) => ({ ...prev, isTechnologistLoading: value }));
    } else if (reportName.includes('cashier')) {
      setIsLoading((prev) => ({ ...prev, isCashierLoading: value }));
    }
  };

  const startToastByReport = (reportName: string) => {
    if (reportName.includes('orders')) startToast();
    else if (reportName.includes('wares')) startWaresToast();
    else if (reportName.includes('technologist')) startTechnologistToast();
  };

  const executeExport = async (
    reportName: string,
    reportPath: string,
    params: Record<string, unknown>
  ) => {
    setLoadingByReport(reportName, true);
    try {
      if (reportName.includes('cashier') || reportName === 'food-technologist') {
        const endpoint = reportName === 'food-technologist'
          ? 'admin/food-technologist-export'
          : 'admin/food-order-cashier';
        const fileName = reportName === 'food-technologist'
          ? 'food-technologist-report.xlsx'
          : 'cashier-report.xlsx';
        const { data: file } = await instance.get(
          `${endpoint}?t=${Date.now()}`,
          { params, responseType: 'blob' }
        );
        downloadFile(file, 'xlsx', fileName);
      } else {
        startToastByReport(reportName);
        await downloadReportFoodFile(reportPath, startDate, endDate, false, params);
      }
    } catch (e) {
      toastError(e);
    } finally {
      setLoadingByReport(reportName, false);
    }
  };

  const handleDrawerExport = async (fields: string[]) => {
    if (!activeReport) return;
    await executeExport(activeReport.reportName, activeReport.reportPath, {
      ...currentFilters,
      fields,
    });
  };

  const handleDrawerPresetExport = async (presetId: number) => {
    if (!activeReport) return;
    await executeExport(activeReport.reportName, activeReport.reportPath, {
      preset_id: presetId,
    });
  };

  return (
    <>
      <Dropdown caption={t('actions')}>
        {(closeDropdown) => (
          <Actions>
            <Action
              onClick={() => {
                closeDropdown();
                handleOpenDrawer(reportConfigs[0]);
              }}
            >
              <div className={styles.actionWrapper}>
                {(isLoading.isOrdersLoading || isActive) && (
                  <div className={styles.loaderWrapper}>
                    <LoaderSpinner />
                  </div>
                )}
                <div
                  style={
                    isLoading.isOrdersLoading || isActive ? { opacity: 0 } : {}
                  }
                  className={styles.text}
                >
                  <DownloadIcon />
                  {`${t('download')} ${t('order_report')}`}
                </div>
              </div>
            </Action>
            <Action
              onClick={() => {
                closeDropdown();
                handleOpenDrawer(reportConfigs[1]);
              }}
            >
              <div className={styles.actionWrapper}>
                {(isLoading.isDishesLoading || isWaresActive) && (
                  <div className={styles.loaderWrapper}>
                    <LoaderSpinner />
                  </div>
                )}
                <div
                  style={
                    isLoading.isDishesLoading || isWaresActive
                      ? { opacity: 0 }
                      : {}
                  }
                  className={styles.text}
                >
                  <DownloadIcon />
                  {`${t('download')} ${t('dish_report')}`}
                </div>
              </div>
            </Action>
            <Action
              onClick={() => {
                closeDropdown();
                handleOpenDrawer(reportConfigs[2]);
              }}
            >
              <div className={styles.actionWrapper}>
                {(isLoading.isTechnologistLoading || isTechnologistActive) && (
                  <div className={styles.loaderWrapper}>
                    <LoaderSpinner />
                  </div>
                )}
                <div
                  style={
                    isLoading.isTechnologistLoading || isTechnologistActive
                      ? { opacity: 0 }
                      : {}
                  }
                  className={styles.text}
                >
                  <DownloadIcon />
                  {t('order_assembly')}
                </div>
              </div>
            </Action>
            <Action
              onClick={() => {
                closeDropdown();
                handleOpenDrawer(reportConfigs[3]);
              }}
            >
              <div className={styles.actionWrapper}>
                {isLoading.isCashierLoading && (
                  <div className={styles.loaderWrapper}>
                    <LoaderSpinner />
                  </div>
                )}
                <div
                  style={isLoading.isCashierLoading ? { opacity: 0 } : {}}
                  className={styles.text}
                >
                  <DownloadIcon />
                  {t('cashier_report')}
                </div>
              </div>
            </Action>
            {(
              <Action
                onClick={() => {
                  closeDropdown();
                  handleOpenDrawer(reportConfigs[4]);
                }}
              >
                <div className={styles.actionWrapper}>
                  {isLoading.isFoodTechnologistLoading && (
                    <div className={styles.loaderWrapper}>
                      <LoaderSpinner />
                    </div>
                  )}
                  <div
                    style={isLoading.isFoodTechnologistLoading ? { opacity: 0 } : {}}
                    className={styles.text}
                  >
                    <DownloadIcon />
                    {t('technologist_report')}
                  </div>
                </div>
              </Action>
            )}
            {onOpenDishesClick ? (
              <Action
                variant="danger"
                onClick={() => {
                  closeDropdown();
                  onOpenDishesClick();
                }}
              >
                <DeleteIcon />
                {t('cancel_dishes_title')}
              </Action>
            ) : (
              ''
            )}
          </Actions>
        )}
      </Dropdown>
      {activeReport && (
        <ReportDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          reportType={activeReport.reportType}
          onExport={handleDrawerExport}
          onPresetExport={handleDrawerPresetExport}
          extraParams={currentFilters}
          title={activeReport.title}
        />
      )}
    </>
  );
};

export default FoodExportActions;
